'use client';

import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { usePathname } from '@/i18n/routing';

import { dataLayerPushAll, EVENT_USER_PROPERTY } from '@/lib/data-layer-push';
import { getServerEnv } from '@/lib/constants/environment';
import { AUTH_SESSION_KEY, OAUTH_REDIRECT_KEY } from '@/lib/constants/session-storage';
import { getLocation } from '@/utils/get-location';

const { GENESIS_API_URL } = getServerEnv();

export interface User {
  email: string;
  userId: string;
  name: {
    familyName: string;
    givenName: string;
    fullName: string;
  };
}

export interface IAuthContext {
  user: User | null | undefined;
  isLoading: boolean;
  isError: boolean;
  signIn: () => string;
  signOut: () => string;
  auth?: {
    genesis?: {
      access_token: string;
    };
    gateway?: {
      token: string;
    };
  } | null;
}

export const AuthContext = React.createContext<IAuthContext>({
  user: undefined,
  isLoading: false,
  isError: false,
  signIn: () => '',
  signOut: () => '',
});

export const AuthProvider = ({ children }) => {
  const pathname = usePathname() || '/';
  const [isLoading, setIsLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [user, setUser] = React.useState<User | null>();

  const [auth, setAuth] = React.useState<IAuthContext['auth'] | null | undefined>(null);

  const signIn = (): string => {
    const signInParams = new URLSearchParams({
      client_id: 'docs-unity',
      response_type: 'code',
      redirect_uri: `${getLocation().origin}/oauth`,
    }).toString();

    sessionStorage.setItem(OAUTH_REDIRECT_KEY, pathname);
    const authHref = `${GENESIS_API_URL}/v1/oauth2/authorize?${signInParams}`;
    getLocation().href = authHref;
    return authHref;
  };

  const signOut = (): string => {
    const authSession = JSON.parse(sessionStorage.getItem(AUTH_SESSION_KEY) || '{}') as IAuthContext['auth'];

    if (typeof window !== 'undefined') {
      sessionStorage.clear();
    }

    const signOutParams: string = new URLSearchParams({
      access_token: authSession?.genesis?.access_token || '',
      post_logout_redirect_uri: `${getLocation().origin}${pathname}`,
    }).toString();

    const signOutHref = `${GENESIS_API_URL}/v1/oauth2/end-session?${signOutParams}`;
    getLocation().href = signOutHref;
    return signOutHref;
  };

  React.useEffect(() => {
    const checkAuthSession = async () => {
      try {
        const authSession = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (authSession) {
          const payload = JSON.parse(authSession);
          setAuth(payload);
        }

        const response = await fetch(`${GENESIS_API_URL}/v1/oauth2/authorize/logined-users`, {
          credentials: 'include',
        });
        const data = await response.json();

        if (!data.model.users.length || !data.model.users[0].login) {
          setUser(null);
        } else {
          setUser(data.model.users[0]);

          dataLayerPushAll([
            {
              event: EVENT_USER_PROPERTY,
              event_name: 'update_user_unity_id',
              properties: {
                old_value: undefined,
                new_value: data.model.users[0].userId,
              },
            },
            {
              event: EVENT_USER_PROPERTY,
              event_name: 'update_user_logged_in',
              properties: {
                old_value: 'no',
                new_value: 'yes',
              },
            },
          ]);
        }
      } catch (error) {
        setIsError(true);
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthSession();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, isError, signIn, signOut, auth }}>{children}</AuthContext.Provider>
  );
};
